import { useMemo } from 'react';
import { useSubscriptions } from '@/hooks/api-hooks';
import { useTranslation } from 'react-i18next';
import { formatPrice, setWebTitle, formatDayLong } from '@/lib/format-utils';
import { SUBSCRIPTIONS_PATH } from '@/lib/navigation';
import { Link } from 'react-router-dom';
import { Badge, Button, Modal } from '@mantine/core';
import { SkeletonList } from '@/components/snippets';
import { IconPlus } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import ModalNewSubscription from '@/components/modal-new-subscription';
import { Subscription } from '@/types/subscription-models';

export const SubscriptionsPage = () => {
    const { t } = useTranslation();
    const { data: subscriptions, isLoading } = useSubscriptions();
    const [newExpenseOpened, { open: openNewExpense, close: closeNewExpense }] = useDisclosure(false);

    setWebTitle(t('subscriptions'));

    const renderSubscriptionRow = (subscription: Subscription) => {
        const sortedPayments = [...(subscription.payments || [])].sort(
            (a, b) => new Date(b.payment_date).getTime() - new Date(a.payment_date).getTime()
        );
        const lastPayment = sortedPayments[0];

        // Determine next due date
        const today = new Date();
        const dueDay = subscription.due_day || 1;
        const hasPaymentThisMonth = subscription.payments?.some((p: any) => {
            const pDate = new Date(p.payment_date);
            return pDate.getMonth() === today.getMonth() && pDate.getFullYear() === today.getFullYear();
        });

        const nextDueDate = hasPaymentThisMonth
            ? new Date(today.getFullYear(), today.getMonth() + 1, dueDay)
            : new Date(today.getFullYear(), today.getMonth(), dueDay);

        // Determine status details
        let statusColor = 'green';
        let statusText = t('status_active');

        if (subscription.status === 'overdue') {
            statusColor = 'red';
            statusText = t('status_overdue');
        } else if (subscription.status === 'canceled') {
            statusColor = 'gray';
            statusText = t('status_canceled');
        } else if (subscription.status === 'paused') {
            statusColor = 'yellow';
            statusText = t('status_paused');
        }

        return (
            <Link
                key={subscription.id}
                to={`${SUBSCRIPTIONS_PATH}/${subscription.id}`}
                className="block border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200 no-underline text-inherit"
            >
                <div className="grid grid-cols-12 gap-4 items-center p-4">
                    {/* Name */}
                    <div className="col-span-12 md:col-span-3">
                        <div className="font-bold text-gray-800 text-sm truncate">{subscription.name}</div>
                    </div>

                    {/* Price */}
                    <div className="col-span-6 md:col-span-2">
                        <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider md:hidden">{t('price')}</div>
                        <div className="font-semibold text-gray-700 text-sm">
                            {formatPrice(subscription.price)} <span className="text-xs text-gray-500 uppercase">{subscription.currency}</span>
                        </div>
                    </div>

                    {/* Next Payment */}
                    <div className="col-span-6 md:col-span-2">
                        <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider md:hidden">{t('next_payment')}</div>
                        <div className="text-xs text-gray-600">
                            {subscription.status !== 'canceled' ? (
                                <div>
                                    <span className="font-semibold text-gray-700">{formatDayLong(nextDueDate)}</span>
                                    <span className="text-gray-400 block text-[10px]">{t('day')} {subscription.due_day}</span>
                                </div>
                            ) : (
                                <span className="text-gray-400">--</span>
                            )}
                        </div>
                    </div>

                    {/* is_automatic */}
                    <div className="col-span-6 md:col-span-2">
                        <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider md:hidden">{t('is_automatic')}</div>
                        <div className="text-xs text-gray-600">
                            {subscription.auto_payment ? (
                                <Badge color="green" variant="outline" size="sm">{t('yes')}</Badge>
                            ) : (
                                <Badge color="gray" variant="outline" size="sm">{t('no')}</Badge>
                            )}
                        </div>
                    </div>

                    {/* Status */}
                    <div className="col-span-6 md:col-span-2 md:text-right">
                        <Badge color={statusColor} variant="filled" size="sm">
                            {statusText}
                        </Badge>
                    </div>
                </div>
            </Link>
        );
    };

    const renderCycleSection = (cycle: 'monthly' | 'yearly') => {
        const cycleSubs = (subscriptions?.data || []).filter((s: any) => s.cycle === cycle);

        const sums: Record<string, number> = {};
        cycleSubs.forEach((sub: any) => {
            if (sub.status !== 'canceled') {
                sums[sub.currency] = (sums[sub.currency] || 0) + Number(sub.price || 0);
            }
        });
        const cycleTotals = Object.entries(sums).map(([currency, amount]) => ({ currency, amount }));

        return (
            <div key={cycle} className="space-y-4">
                <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                    <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t(`cycle_${cycle}`)}</h2>
                    {cycleTotals.length > 0 && (
                        <div className="flex gap-2">
                            {cycleTotals.map(({ currency, amount }) => (
                                <Badge key={currency} color="gray" variant="light" size="sm" className="font-semibold">
                                    {t('total')}: {formatPrice(amount)} <span className="uppercase">{currency}</span>
                                </Badge>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex flex-col border border-gray-200 rounded-md overflow-hidden bg-white">
                    <div className="hidden md:grid grid-cols-12 gap-4 items-center bg-gray-50 p-4 border-b border-gray-200 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                        <div className="col-span-3">{t('name')}</div>
                        <div className="col-span-2">{t('price')}</div>
                        <div className="col-span-2">{t('next_payment')}</div>
                        <div className="col-span-2">{t('auto_payment')}</div>
                        <div className="col-span-2 text-right">{t('status')}</div>
                    </div>

                    {cycleSubs.map(renderSubscriptionRow)}

                    {cycleSubs.length === 0 && (
                        <div className="p-8 text-center text-gray-400 text-sm italic bg-white">
                            {t('no_results')}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div>
            <div className="panel-content">
                <div className="flex justify-between items-center mb-6 gap-4 flex-wrap">
                    <h1 className="text-2xl font-bold text-gray-900">{t('expenses')}</h1>
                    <Button
                        size="xs"
                        color="black"
                        leftSection={<IconPlus size={16} />}
                        onClick={openNewExpense}
                    >
                        {t('create_recurring_expense')}
                    </Button>
                </div>

                {isLoading ? (
                    <SkeletonList />
                ) : (
                    <div className="space-y-10">
                        {renderCycleSection('monthly')}
                        {renderCycleSection('yearly')}
                    </div>
                )}
            </div>

            <Modal
                opened={newExpenseOpened}
                onClose={closeNewExpense}
                title={t('create_recurring_expense')}
                size="lg"
            >
                <ModalNewSubscription onClose={closeNewExpense} />
            </Modal>
        </div>
    );
};

export default SubscriptionsPage;
